import React from 'react';

const PropertyTag = ({
  propertyKey,
  value,
  list,
  onManageAccess,
  onPropertySort,
  customPrompts = []
}) => {
  // Get color from prompts by matching property key to propertyName
  const getPropertyColor = (key) => {
    const lowerKey = key.toLowerCase();

    for (const prompt of customPrompts) {
      // New structure: use explicit propertyName field
      if (prompt.propertyName) {
        if (prompt.propertyName.toLowerCase() === lowerKey) {
          return prompt.color;
        }
        continue;
      }

      // Legacy fallback: try to match from text
      const promptText = typeof prompt === 'string' ? prompt : prompt.text;
      const promptColor = typeof prompt === 'string' ? null : prompt.color;

      if (!promptText) continue;

      // Try to extract and match property name from text
      const patterns = [
        new RegExp(`property called ['"]${lowerKey}['"]`, 'i'),
        new RegExp(`property named ['"]${lowerKey}['"]`, 'i'),
      ];

      for (const pattern of patterns) {
        if (pattern.test(promptText)) {
          return promptColor;
        }
      }
    }
    return null;
  };

  const formatPropertyDisplay = (key, val) => {
    // Skip null, undefined, or empty values
    if (val === null || val === undefined || val === '') {
      return null;
    }
    
    // Skip nested objects completely
    if (typeof val === 'object' && val !== null) {
      return null;
    }
    
    if (key === 'qty' || key === 'quantity') {
      // Don't show quantity if it's empty, "0", or starts with "0"
      if (!val || val === '0' || val === 0 || val.toString().match(/^0+$/)) return null;
      return `Qty: ${val}`;
    }
    if (key === 'date' || key === 'due_date' || key === 'scheduled') {
      // Handle date formatting - skip null dates
      if (!val) return null;
      const dateStr = typeof val === 'string' ? val : val.toString();
      return `📅 ${dateStr}`;
    }
    if (key.startsWith('name_')) {
      // Don't display translation properties as tags - they should be inline only
      return null;
    }
    if (key === 'language_preference') {
      return null; // Don't display this internal property
    }
    if (key.startsWith('tag_') || (typeof val === 'string' && val.startsWith('#'))) {
      return val.startsWith('#') ? val : `#${val}`;
    }
    if (key === 'shared') {
      return `🤝 Shared (${val})`;
    }
    if (key === 'public') {
      return val ? '🌐 Public' : null;
    }
    if (key === 'public_editable') {
      return val ? '✏️ Public Editable' : '👁️ Public View-Only';
    }
    if (key === 'permission') {
      const permissionEmojis = { view: '👁️', edit: '✏️', admin: '👑' };
      return `${permissionEmojis[val] || '🔑'} ${val}`;
    }
    if (key.startsWith('shared_')) {
      return val; // value is already formatted as "shared: email"
    }
    if (typeof val === 'boolean') {
      return val ? key : null;
    }
    if (typeof val === 'number' && key !== 'qty' && key !== 'quantity') {
      return `${key}: ${val}`;
    }
    // Handle specific property keys
    if (key === 'department' || key === 'section') {
      return `📦 ${val}`;
    }
    if (key === 'category') {
      return `🏷️ ${val}`;
    }
    if (key === 'aisle') {
      return `🛒 ${val}`;
    }
    if (key === 'emoji' && val) {
      return val; // Just show the emoji
    }
    // For other properties, just show the value
    return `${val}`;
  };

  const displayText = formatPropertyDisplay(propertyKey, value);
  if (!displayText) return null;

  const getPropertyClass = (key, val) => {
    if (key === 'qty' || key === 'quantity') return 'qty';
    if (key === 'department' || key === 'section') return 'department';
    if (key === 'category') return 'category';
    if (key === 'priority') return 'priority';
    if (key === 'date' || key === 'due_date' || key === 'scheduled') return 'date';
    if (key.startsWith('name_')) return 'language';
    if (key.startsWith('tag_') || (typeof val === 'string' && val.startsWith('#'))) return 'tag';
    if (key === 'shared' || key === 'permission' || key.startsWith('shared_')) return 'shared';
    if (key === 'public' || key === 'public_editable') return 'public';
    return '';
  };

  const handleClick = (e) => {
    if (propertyKey.startsWith('shared_') && onManageAccess && list) {
      e.stopPropagation(); // Prevent list selection
      const email = value.replace('shared: ', ''); // Extract email from "shared: email"
      onManageAccess(list, propertyKey, email);
    } else if (onPropertySort) {
      e.stopPropagation(); // Prevent item completion toggle
      onPropertySort(propertyKey, value);
    }
  };

  const isClickable = (propertyKey.startsWith('shared_') && onManageAccess && list) || onPropertySort;
  const customColor = getPropertyColor(propertyKey);

  // Build inline style with !important equivalents by using style object
  const inlineStyle = {
    ...(isClickable ? { cursor: 'pointer' } : {}),
    ...(customColor ? {
      backgroundColor: customColor,
      color: 'white',
      borderColor: customColor
    } : {})
  };

  return (
    <span
      className={`property-tag ${customColor ? 'custom-color' : getPropertyClass(propertyKey, value)} ${isClickable ? 'clickable' : ''}`}
      onClick={isClickable ? handleClick : undefined}
      style={inlineStyle}
    >
      {displayText}
    </span>
  );
};

export default PropertyTag; 